import { useCallback, useEffect, useRef, useState } from "react";
import { Maximize, Minimize, RectangleHorizontal, RectangleVertical } from "lucide-react";
import HeroYoutubePlayer from "./HeroYoutubePlayer";
import MediosVolumeControls from "./MediosVolumeControls";
import { useMediosChannelAudio } from "../hooks/useMediosChannelAudio";
import ConsentAwareEmbed from "./ConsentAwareEmbed";

function enterFullscreen(el) {
  if (!el) return Promise.resolve();
  if (el.requestFullscreen) return el.requestFullscreen();
  if (el.webkitRequestFullscreen) return el.webkitRequestFullscreen();
  return Promise.reject(new Error("fullscreen"));
}

function exitFullscreen() {
  if (document.exitFullscreen) return document.exitFullscreen();
  if (document.webkitExitFullscreen) return document.webkitExitFullscreen();
  return Promise.resolve();
}

function isFullscreenEl(el) {
  const cur = document.fullscreenElement || document.webkitFullscreenElement;
  return Boolean(el && cur === el);
}

const chromeBtn =
  "pointer-events-auto inline-flex items-center justify-center rounded-full border border-white/25 bg-black/55 text-white backdrop-blur-md hover:bg-black/80 p-2";

export default function MediosPhonePlayer({
  clips,
  title,
  posterUrl,
  width = 280,
  height = 640,
  marcoColor = "#ffffff",
  marcoAncho = 10,
}) {
  const { muted, volume, setMutedExclusive, setVolumeLevel } = useMediosChannelAudio("hub");
  const [captions, setCaptions] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [hover, setHover] = useState(false);
  const [orientLock, setOrientLock] = useState(null);
  const [clipOrient, setClipOrient] = useState(
    clips?.[0]?.orientacion === "horizontal" ? "horizontal" : "vertical"
  );
  const [touchUi, setTouchUi] = useState(false);
  const stageRef = useRef(null);
  const titleText = String(title || "").trim();

  const onClipChange = useCallback((clip) => {
    setClipOrient(clip?.orientacion === "horizontal" ? "horizontal" : "vertical");
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(hover: none), (pointer: coarse)");
    const sync = () => setTouchUi(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    const sync = () => setFullscreen(isFullscreenEl(stageRef.current));
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (isFullscreenEl(stageRef.current)) await exitFullscreen();
      else await enterFullscreen(stageRef.current);
    } catch {
      /* política del navegador */
    }
  };

  if (!clips?.length) return null;

  const showChrome = hover || fullscreen || touchUi;
  const orient = orientLock || clipOrient;
  const isVertical = orient !== "horizontal";
  const boxW = isVertical ? width : Math.round(width * (16 / 9));
  const boxH = isVertical ? height : width;

  return (
    <div
      className="medios-col flex h-auto min-h-0 w-full max-w-full flex-col items-center lg:h-full"
      style={{
        "--medios-w": `${boxW}px`,
        "--medios-h": `${boxH}px`,
        "--medios-frame": `${marcoAncho}px`,
        "--medios-frame-color": marcoColor,
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocusCapture={() => setHover(true)}
      onTouchStart={() => setHover(true)}
    >
      <h3 className="medios-phone-title font-display text-base sm:text-lg font-bold text-brand-dark mb-3 w-full text-center leading-snug shrink-0 whitespace-pre-line">
        {titleText}
      </h3>

      <div className="medios-phone-row flex w-full max-w-full items-start justify-center lg:min-h-0 lg:flex-1">
        <div
          ref={stageRef}
          className="medios-phone relative shrink-0"
          data-orient={isVertical ? "vertical" : "horizontal"}
        >
          <div className="medios-phone__screen relative bg-[#05190f]">
            <div className="absolute inset-0 overflow-hidden rounded-[inherit]">
              <ConsentAwareEmbed
                title="Video de medios"
                description="Este reproductor carga YouTube o Facebook. Actívelo si acepta cookies de terceros."
                className="absolute inset-0 rounded-none h-full border-0"
                minHeight="100%"
              >
                <HeroYoutubePlayer
                  className="absolute inset-0"
                  layout="phone"
                  frameOrientacion={isVertical ? "vertical" : "horizontal"}
                  clips={clips}
                  muted={muted}
                  volume={volume}
                  captions={captions}
                  fallbackPoster={posterUrl}
                  title={titleText.replace(/\n/g, " ")}
                  onClipChange={onClipChange}
                />
              </ConsentAwareEmbed>
            </div>
            <div
              className={`absolute left-2 top-2 z-20 flex flex-col items-start gap-1.5 transition-opacity duration-200 ${
                showChrome ? "opacity-100" : "pointer-events-none opacity-0"
              }`}
            >
              <MediosVolumeControls
                muted={muted}
                volume={volume}
                onMuteToggle={() => setMutedExclusive(!muted)}
                onVolumeLevel={setVolumeLevel}
                buttonClass={chromeBtn}
              />
              <button
                type="button"
                onClick={() => setOrientLock("vertical")}
                className={`${chromeBtn} ${isVertical ? "border-brand-accent text-brand-accent" : ""}`}
                aria-pressed={isVertical}
                aria-label="Formato vertical (Short)"
                title="Vertical"
              >
                <RectangleVertical size={16} />
              </button>
              <button
                type="button"
                onClick={() => setOrientLock("horizontal")}
                className={`${chromeBtn} ${!isVertical ? "border-brand-accent text-brand-accent" : ""}`}
                aria-pressed={!isVertical}
                aria-label="Formato horizontal"
                title="Horizontal"
              >
                <RectangleHorizontal size={16} />
              </button>
              <button
                type="button"
                onClick={() => void toggleFullscreen()}
                className={chromeBtn}
                aria-pressed={fullscreen}
                aria-label={fullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
                title={fullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
              >
                {fullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
              </button>
              <button
                type="button"
                onClick={() => setCaptions((v) => !v)}
                className={`${chromeBtn} ${
                  captions ? "border-brand-accent bg-black/80 text-brand-accent" : ""
                }`}
                aria-pressed={captions}
                aria-label={captions ? "Ocultar subtítulos" : "Mostrar subtítulos"}
                title="Subtítulos (CC)"
              >
                <span className="text-[10px] font-black tracking-widest">CC</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
